<template>
  <div ref="scroller" class="message-list" :class="{ 'is-compact': compact }">
    <div v-if="isPending" class="message-list__hint">
      <i class="fas fa-spinner fa-spin tw-mr-1" /> Loading messages…
    </div>
    <ErrorNotice v-else-if="error" :error-message="describePatientConversationError(error)" />

    <template v-else>
      <div v-if="hasNextPage" class="message-list__earlier">
        <el-button link size="small" :loading="isFetchingNextPage" @click="fetchNextPage()">
          Load earlier messages
        </el-button>
      </div>
      <div v-else-if="!messages.length" class="message-list__hint">No messages yet</div>

      <ConversationMessageItem
        v-for="message in messages"
        :key="message.id"
        :message="message"
        :patient-name="patientName"
        :compact="compact"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { PatientConversationMessageDTO } from "@vitable/asclepius-api-client-ts"
import ErrorNotice from "@/components/ErrorNotice.vue"
import ConversationMessageItem from "@/modules/patient-conversations/components/thread/ConversationMessageItem.vue"
import { usePatientConversationMessages } from "@/modules/patient-conversations/composables/patient-conversations"
import { describePatientConversationError } from "@/modules/patient-conversations/constants/patient-conversation-error-codes"

const props = defineProps<{
  patientConversationId: string
  patientName: string
  compact?: boolean
}>()

const conversationId = computed(() => props.patientConversationId)
const { data, isPending, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
  usePatientConversationMessages(conversationId)

// Pages arrive newest first; the thread reads oldest to newest with the latest message at the bottom.
const messages = computed<Array<PatientConversationMessageDTO>>(() =>
  (data.value?.pages ?? []).flatMap((page) => page.data).reverse()
)

const scroller = ref<HTMLElement>()
const latestMessageId = computed<string | undefined>(() => messages.value[messages.value.length - 1]?.id)

// A new conversation or a new latest message scrolls to the bottom; loading older pages keeps the position.
watch(latestMessageId, async () => {
  await nextTick()
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight
})
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.message-list
  display: flex
  flex-direction: column
  flex: 1 1 auto
  gap: 6px
  min-height: 0
  overflow-y: auto
  padding: 8px 12px

  &.is-compact
    padding: 8px 8px

  &__hint
    padding: 24px 0
    text-align: center
    font-size: 13px
    color: $grey

  &__earlier
    display: flex
    justify-content: center
    padding: 4px 0 8px
</style>
